import { CheckCircleIcon, CircleNotchIcon, WarningCircleIcon } from '@phosphor-icons/react'
import { Link } from 'react-router'
import { match } from 'ts-pattern'
import { Routes } from '../../routes.ts'
import {
  FREEFORM_SESSIONS_PAGE_SIZE,
  isFreeformUploadSessionStalled,
  useGetFreeformUploadSessions,
} from '../../utils/api-client/api-hooks.ts'
import type { FreeformUploadSessionSummaryResponse } from '../../utils/api-client/api-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { pluralize } from '../../utils/text-utils.ts'
import * as styles from './freeform-recent-runs.css.ts'

type FreeformRecentRunsProps = {
  organizationId?: string
}

/** The organization's latest Findings Intake runs, each linking to its run page. Nothing at all before the first. */
export const FreeformRecentRuns: React.FC<FreeformRecentRunsProps> = ({ organizationId }) => {
  const { data } = useGetFreeformUploadSessions({ organizationId })
  const sessions = data?._embedded.items ?? []
  const total = data?.total ?? sessions.length
  if (sessions.length === 0) {
    return null
  }

  return (
    <section className={styles.section} aria-label="Active and recent runs">
      <div className={styles.heading}>
        <h2 className={styles.title}>Active and recent runs</h2>
        {total > FREEFORM_SESSIONS_PAGE_SIZE && (
          <span className={styles.meta}>
            Latest {FREEFORM_SESSIONS_PAGE_SIZE} of {total}
          </span>
        )}
      </div>
      <ul className={styles.list}>
        {sessions.map(session => (
          <li key={session.id}>
            <Link className={styles.row} to={Routes.FreeformRunPage.createPath({ sessionId: session.id })}>
              <StatusPill session={session} />
              <span className={styles.text}>
                <span className={styles.files} title={session.filenames.join(', ')}>
                  {session.filenames.join(', ') || 'Upload'}
                </span>
                <span className={styles.detail}>
                  {session.created_by ? `${session.created_by} · ` : ''}
                  {formatDate({ date: session.created_at, type: 'mm/dd/yyyy-hh:mm' })}
                </span>
              </span>
              <span className={styles.counts}>{countsOf(session)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

const countsOf = (session: FreeformUploadSessionSummaryResponse) =>
  match(session.status)
    .with('parsing', 'associating', () =>
      isFreeformUploadSessionStalled(session) ? 'Stopped reporting before it finished' : 'In progress'
    )
    .with('failed', () => session.failure_reason ?? 'Stopped before it finished')
    .with('completed', () =>
      [
        `${session.scan_count} ${pluralize(session.scan_count, 'scan', 'scans')}`,
        `${session.finding_count} ${pluralize(session.finding_count, 'finding', 'findings')}`,
        session.unassociated_count > 0 ? `${session.unassociated_count} need a human look` : undefined,
      ]
        .filter(part => part !== undefined)
        .join(' · ')
    )
    .exhaustive()

const statusOf = (session: FreeformUploadSessionSummaryResponse) =>
  match(session.status)
    .when(
      () => isFreeformUploadSessionStalled(session),
      () => ({ variant: 'failed' as const, Icon: WarningCircleIcon, label: 'Stopped' })
    )
    .with('parsing', 'associating', () => ({ variant: 'running' as const, Icon: CircleNotchIcon, label: 'Running' }))
    .with('completed', () => ({ variant: 'completed' as const, Icon: CheckCircleIcon, label: 'Complete' }))
    .with('failed', () => ({ variant: 'failed' as const, Icon: WarningCircleIcon, label: 'Failed' }))
    .exhaustive()

const StatusPill: React.FC<{ session: FreeformUploadSessionSummaryResponse }> = ({ session }) => {
  const { variant, Icon, label } = statusOf(session)
  return (
    <span className={styles.status({ status: variant })}>
      {variant === 'running' ? (
        <Icon size={12} className={styles.spinner} aria-hidden />
      ) : (
        <Icon size={12} weight="fill" aria-hidden />
      )}
      {label}
    </span>
  )
}
